import { type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';

import { RUNTIME_CONFIG } from '../shell/runtime/config';

/**
Matches the sentinel prefix Orval bakes into every generated request URL,
e.g. `/__masterdata/v1/brands`. The `__` prefix cannot collide with a real
API path, so an unrewritten URL is unmistakable in DevTools.

Kept in step with `urlMarker` in tools/api-registry.ts.
*/
const API_MARKER_PATTERN = /^\/__([a-z0-9-]+)(?=\/)/u;

/**
Rewrites generated API URLs onto their configured, origin-relative base.

Generated clients carry a sentinel prefix rather than a host, so the same
bundle runs against any environment. This interceptor swaps that prefix for
the base URL supplied at bootstrap: `/__masterdata/v1/brands` becomes
`/keyoris/ima/v1/brands`.

An unconfigured API fails the request rather than passing it through. That is
deliberate: the deployed origin answers unmatched `/v1/*` paths with the SPA's
own index.html and HTTP 200, so a silent pass-through would surface much later
as a confusing JSON parse error instead of a configuration problem.

The failure is delivered as an errored stream rather than a thrown exception,
so it reaches consumers through the normal channel — a subscriber's error
callback, or an `httpResource`'s `error()` signal. A synchronous throw here
would escape the HTTP pipeline unhandled and never reach either.

Non-generated requests (i18n catalogue fetches, anything hand-written) carry
no marker and pass through untouched.
@param request - Outgoing request.
@param next - Next handler in the interceptor chain.
@returns The handler's stream, with the API base URL resolved where applicable.
*/
export const apiBaseUrlInterceptor: HttpInterceptorFn = (request, next) => {
  const match = API_MARKER_PATTERN.exec(request.url);

  if (match === null) {
    return next(request);
  }

  const [marker, apiName = ''] = match;
  /*
   * Widened to optional values on purpose: the key comes from the request URL,
   * so a miss is reachable at runtime. Record<string, string> would otherwise
   * type the lookup as always-present and make the guard below look dead.
   */
  const configuredBaseUrls: Readonly<Record<string, string | undefined>> =
    inject(RUNTIME_CONFIG).apiBaseUrls;
  // eslint-disable-next-line security/detect-object-injection -- key is constrained to [a-z0-9-]+ by API_MARKER_PATTERN
  const baseUrl = configuredBaseUrls[apiName];

  if (baseUrl === undefined) {
    return throwError(
      () =>
        new Error(
          `No runtime base URL configured for API "${apiName}". ` +
            'The api-base-urls runtime configuration must supply it.',
        ),
    );
  }

  return next(
    request.clone({
      url: `${baseUrl}${request.url.slice(marker.length)}`,
      withCredentials: true,
    }),
  );
};
